import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Modal } from '~/components/overlays/Modal';
import { Button } from '~/components/ui';
import { SellerListSelect } from '~/components/common/SellerListSelect';
import { SellerSelectTable } from '~/components/common/SellerSelectTable';
import { useAddTradingPartnersToProductComparison } from '~/api/organizations';
import type { SellerList } from '~/api/sellerLists';
import { toggleId } from '~/utils/toggleId';
import { type ClientError, extractGraphQLErrorMessages } from '~/utils';

type AddSellerModalProps = {
  isOpen: boolean;
  onClose: () => void;
  platformProductId: string;
  platformProductLengthIds?: string[];
  excludeOrganizationIds: string[];
  onSellerAdded?: () => void;
};

export function AddSellerModal({
  isOpen,
  onClose,
  platformProductId,
  platformProductLengthIds,
  excludeOrganizationIds,
  onSellerAdded,
}: Readonly<AddSellerModalProps>) {
  const { t } = useTranslation('order-builder');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { mutate: addSellers, isPending } =
    useAddTradingPartnersToProductComparison();

  const lengthCount = platformProductLengthIds?.length ?? 0;

  const addOrganizations = (organizationIds: string[]) => {
    addSellers(
      {
        input: {
          organizationIds,
          platformProductLengthIds,
          platformProductId,
        },
      },
      {
        onSuccess: (data) => {
          const { configurations, addedOrganizationIds } =
            data.addTradingPartnersToProductComparison;
          const addedCount = addedOrganizationIds.length;
          if (configurations.length === 0) {
            // The mutation skips organizations with no facility or location, so an empty result
            // is a real outcome and not an error the caller would otherwise hear about.
            toast.error(t('order_builder.add_seller.none_added'));
          } else if (addedCount === 0) {
            toast(t('order_builder.add_seller.already_added'));
          } else {
            toast.success(
              addedCount > 1
                ? t('order_builder.add_seller.success_multiple', {
                    count: addedCount,
                  })
                : t('order_builder.add_seller.success')
            );
          }
          onSellerAdded?.();
          onClose();
        },
        onError: (error) =>
          toast.error(
            extractGraphQLErrorMessages(error as ClientError).join(', ')
          ),
      }
    );
  };

  const applySellerList = (sellerList: SellerList) =>
    addOrganizations([
      ...new Set([
        ...selectedIds,
        ...sellerList.members.map(({ seller }) => seller.id),
      ]),
    ]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="bg-neut-light rounded-lg shadow-xl max-w-2xl w-full mx-4 h-[min(600px,90vh)] flex flex-col">
        <div className="px-6 py-4 border-b border-neut-border shrink-0">
          <h2 className="text-lg font-semibold text-neut-prim">
            {t('order_builder.add_seller.title')}
          </h2>
          <p
            className="mt-1 text-sm text-neut-sec"
            data-testid="add-seller-scope"
          >
            {lengthCount === 0
              ? t('order_builder.add_seller.scope_product')
              : lengthCount === 1
                ? t('order_builder.add_seller.scope_single')
                : t('order_builder.add_seller.scope_multiple', {
                    count: lengthCount,
                  })}
          </p>
        </div>

        <SellerListSelect
          testId="add-seller-apply-seller-list"
          className="px-6 py-3 border-b border-neut-border shrink-0"
          disabled={isPending}
          labels={{
            apply: t('order_builder.add_seller.apply_seller_list'),
            option: (name, count) =>
              t('order_builder.add_seller.seller_list_option', { name, count }),
          }}
          onApply={applySellerList}
        />

        <SellerSelectTable
          testId="add-seller"
          labels={{
            organization: t('order_builder.add_seller.organization'),
            searchPlaceholder: t('order_builder.add_seller.search_placeholder'),
            searchLabel: t('order_builder.add_seller.search_label'),
            loading: t('order_builder.add_seller.loading'),
            empty: t('order_builder.add_seller.empty'),
            error: t('order_builder.add_seller.error'),
            select: (name) =>
              t('order_builder.add_seller.select_organization', { name }),
          }}
          selectedIds={selectedIds}
          onToggle={(id) =>
            setSelectedIds((previous) => toggleId(previous, id))
          }
          excludeOrganizationIds={excludeOrganizationIds}
        />

        <div className="px-6 py-4 border-t border-neut-border flex items-center justify-between gap-3 shrink-0">
          <Button
            variant="secondary"
            onClick={onClose}
            data-testid="add-seller-cancel-button"
          >
            {t('order_builder.add_seller.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => addOrganizations(Array.from(selectedIds))}
            disabled={selectedIds.size === 0 || isPending}
            data-testid="add-seller-submit-button"
          >
            {t('order_builder.add_seller.add_selected', {
              count: selectedIds.size,
            })}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
